import { useEffect, useState, useCallback, useRef } from "react";
import client, { API } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Paperclip, Trash2, Loader2, Download, Upload, FileText, Image as ImageIcon, Pencil, Check, X, Eye } from "lucide-react";
import { Avatar } from "@/components/shared/UI";
import { DeleteButton } from "@/components/shared/DeleteButton";
import { usePasteImages } from "@/lib/usePasteImages";

function whenLabel(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

function sizeLabel(bytes) {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const MAX_BYTES = 10 * 1024 * 1024;

const isImage = (a) => (a.content_type || "").startsWith("image/") || /\.(png|jpe?g|gif|webp)$/i.test(a.original_filename || "");
const isPdf = (a) => (a.content_type || "") === "application/pdf" || /\.pdf$/i.test(a.original_filename || "");
const canPreview = (a) => isImage(a) || isPdf(a);

function srcUrl(a, { inline } = {}) {
  // Auth travels via the HttpOnly cookie (same-origin requests send it automatically).
  return `${API}/attachments/${a.id}/download${inline ? "?inline=1" : ""}`;
}

function PreviewModal({ attachment, onClose }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!attachment) return undefined;
    let obj = null;
    setUrl(null); setFailed(false);
    client.get(`/attachments/${attachment.id}/download`, { params: { inline: 1 }, responseType: "blob" })
      .then(({ data }) => {
        const type = isPdf(attachment) ? "application/pdf" : data.type;
        obj = URL.createObjectURL(new Blob([data], { type }));
        setUrl(obj);
      })
      .catch(() => setFailed(true));
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [attachment]);
  if (!attachment) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-hidden" data-testid="attachment-preview-modal">
        <DialogHeader>
          <DialogTitle className="truncate pr-6 font-heading text-sm">{attachment.name}</DialogTitle>
          <DialogDescription className="sr-only">Preview of {attachment.original_filename}</DialogDescription>
        </DialogHeader>
        <div className="mt-2 flex justify-center">
          {failed ? <p className="py-16 text-sm text-slate-500" data-testid="attachment-preview-failed">Could not load the preview. Try downloading the file instead.</p>
          : !url ? <Loader2 className="my-16 h-6 w-6 animate-spin text-slate-400" />
          : isPdf(attachment) ? (
            <iframe title={attachment.name} src={url} className="h-[75vh] w-full rounded-md border border-slate-200" data-testid="attachment-preview-pdf" />
          ) : (
            <img src={url} alt={attachment.name} className="max-h-[75vh] w-auto rounded-md object-contain" data-testid="attachment-preview-img" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Thumb({ attachment, onClick }) {
  const clickable = canPreview(attachment);
  const base = "mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-slate-100 text-slate-500";
  if (isImage(attachment)) {
    return (
      <button type="button" onClick={onClick} className={`${base} ${clickable ? "cursor-pointer" : ""}`} title="Preview">
        <img src={srcUrl(attachment, { inline: true })} alt="" className="h-full w-full object-cover" />
      </button>
    );
  }
  return (
    <button type="button" onClick={clickable ? onClick : undefined} className={`${base} ${clickable ? "cursor-pointer" : "cursor-default"}`} title={clickable ? "Preview" : undefined}>
      {isPdf(attachment) ? <FileText className="h-5 w-5 text-rose-500" /> : <FileText className="h-5 w-5" />}
    </button>
  );
}

export function AttachmentsPanel({ entityType, entityId, simpleNaming = false, anyType = false }) {
  const [items, setItems] = useState([]);
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [preview, setPreview] = useState(null);
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [dropCount, setDropCount] = useState(0);
  const dragDepth = useRef(0);

  const load = useCallback(() => {
    client.get(`/attachments`, { params: { entity_type: entityType, entity_id: entityId } })
      .then(({ data }) => setItems(data))
      .catch(() => toast.error("Could not load attachments"));
  }, [entityType, entityId]);
  useEffect(() => { load(); }, [load]);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_BYTES) {
      toast.error("File exceeds the 10 MB limit");
      e.target.value = "";
      return;
    }
    setFile(f);
    if (!simpleNaming && !name) setName(f.name.replace(/\.[^.]+$/, ""));
  };

  const upload = async () => {
    if (!file || uploading) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("entity_type", entityType);
    fd.append("entity_id", entityId);
    fd.append("file", file);
    fd.append("name", name.trim() || file.name);
    try {
      const { data } = await client.post(`/attachments`, fd);
      setItems((prev) => [data, ...prev]);
      setFile(null);
      setName("");
      if (inputRef.current) inputRef.current.value = "";
      toast.success("Attachment uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not upload file");
    } finally {
      setUploading(false);
    }
  };

  const ACCEPT_RE = /\.(pdf|docx?|xlsx?|pptx?|csv|txt|png|jpe?g|gif|webp)$/i;
  const uploadFiles = async (files) => {
    const ok = files.filter((f) => {
      if (!ACCEPT_RE.test(f.name)) { toast.error(`${f.name}: file type not supported`); return false; }
      if (f.size > MAX_BYTES) { toast.error(`${f.name} exceeds the 10 MB limit`); return false; }
      return true;
    });
    if (!ok.length) return;
    setDropCount(ok.length);
    let done = 0;
    for (const f of ok) {
      const fd = new FormData();
      fd.append("entity_type", entityType);
      fd.append("entity_id", entityId);
      fd.append("file", f);
      fd.append("name", simpleNaming ? f.name : f.name.replace(/\.[^.]+$/, ""));
      try {
        const { data } = await client.post(`/attachments`, fd);
        setItems((prev) => [data, ...prev]);
        done += 1;
      } catch (e) {
        toast.error(`${f.name}: ${e?.response?.data?.detail || "could not upload"}`);
      }
      setDropCount((n) => n - 1);
    }
    if (done) toast.success(done === 1 ? "Attachment uploaded" : `${done} attachments uploaded`);
  };
  const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");
  const dropHandlers = {
    onDragEnter: (e) => { if (!hasFiles(e)) return; e.preventDefault(); dragDepth.current += 1; setDragging(true); },
    onDragOver: (e) => { if (!hasFiles(e)) return; e.preventDefault(); e.dataTransfer.dropEffect = "copy"; },
    onDragLeave: (e) => { if (!hasFiles(e)) return; dragDepth.current = Math.max(0, dragDepth.current - 1); if (!dragDepth.current) setDragging(false); },
    onDrop: (e) => { if (!hasFiles(e)) return; e.preventDefault(); dragDepth.current = 0; setDragging(false); uploadFiles(Array.from(e.dataTransfer.files || [])); },
  };

  const pasteRef = usePasteImages(uploadFiles);

  const download = async (a) => {
    try {
      const res = await client.get(`/attachments/${a.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = a.original_filename || a.name || "download";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download file");
    }
  };

  const startRename = (a) => { setEditingId(a.id); setEditName(a.name); };
  const cancelRename = () => { setEditingId(null); setEditName(""); };
  const saveRename = async (a) => {
    const n = editName.trim();
    if (!n) { toast.error("Name cannot be empty"); return; }
    try {
      const { data } = await client.patch(`/attachments/${a.id}`, { name: n });
      setItems((prev) => prev.map((x) => (x.id === a.id ? data : x)));
      cancelRename();
      toast.success("Attachment renamed");
    } catch (e) {
      toast.error("Could not rename attachment");
    }
  };

  return (
    <div ref={pasteRef} data-testid="attachments-panel" className="relative mt-4" {...dropHandlers}>
      {dragging && (
        <div className="pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-sky-400 bg-sky-50/90 text-sky-700" data-testid="attachment-drop-overlay">
          <Upload className="h-6 w-6" />
          <span className="text-sm font-semibold">Drop files to upload</span>
        </div>
      )}
      <div className="rounded-lg border border-dashed border-slate-200 p-3">
        <input
          ref={inputRef}
          type="file"
          data-testid="attachment-file-input"
          onChange={pick}
          accept={anyType ? undefined : ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.png,.jpg,.jpeg,.gif,.webp"}
          className="hidden"
        />
        <button type="button" onClick={() => inputRef.current?.click()} data-testid="attachment-dropzone"
          className="flex w-full flex-col items-center justify-center gap-1 rounded-md px-3 py-4 text-center text-slate-500 transition-colors hover:bg-slate-50">
          {dropCount > 0 ? <Loader2 className="h-5 w-5 animate-spin text-sky-600" /> : <Upload className="h-5 w-5 text-slate-400" />}
          <span className="text-sm"><span className="font-semibold text-sky-700">{dropCount > 0 ? `Uploading ${dropCount}…` : "Drag & drop files here"}</span>{dropCount > 0 ? "" : " or click to browse"}</span>
          <span className="text-[11px] text-slate-400">{anyType ? "Any file type" : "PDF, Office, images, CSV or TXT"} · up to 10 MB each · or paste an image (Ctrl+V)</span>
        </button>
        {file && (
          <div className="mt-2 space-y-2">
            {simpleNaming ? (
              <p className="truncate text-sm text-slate-600" data-testid="attachment-selected-name">{file.name}</p>
            ) : (
              <input
                type="text"
                data-testid="attachment-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Attachment name"
                className="w-full rounded-md border border-slate-200 px-3 py-1.5 text-sm text-slate-800 placeholder:text-slate-400 focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
              />
            )}
            <button
              onClick={upload}
              disabled={uploading}
              data-testid="attachment-upload-btn"
              className="flex w-full items-center justify-center gap-1.5 rounded-md bg-sky-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-sky-700 disabled:opacity-60"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {uploading ? "Uploading…" : "Upload"}
            </button>
          </div>
        )}
      </div>

      <div className="mt-3 space-y-2" data-testid="attachments-list">
        {items.length === 0 && (
          <p className="flex items-center gap-1.5 px-1 py-2 text-sm text-slate-400">
            <Paperclip className="h-4 w-4" /> No attachments yet.
          </p>
        )}
        {items.map((a) => (
          <div key={a.id} data-testid={`attachment-item-${a.id}`} className="group flex gap-3 rounded-lg border border-slate-100 p-3">
            <Thumb attachment={a} onClick={() => setPreview(a)} />
            <div className="min-w-0 flex-1">
              {editingId === a.id ? (
                <div className="flex items-center gap-1">
                  <input
                    autoFocus
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveRename(a); if (e.key === "Escape") cancelRename(); }}
                    data-testid={`attachment-rename-input-${a.id}`}
                    className="w-full rounded-md border border-slate-200 px-2 py-1 text-sm text-slate-800 focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
                  />
                  <button onClick={() => saveRename(a)} data-testid={`attachment-rename-save-${a.id}`} className="rounded-md p-1 text-emerald-600 hover:bg-emerald-50" title="Save"><Check className="h-4 w-4" /></button>
                  <button onClick={cancelRename} className="rounded-md p-1 text-slate-400 hover:bg-slate-100" title="Cancel"><X className="h-4 w-4" /></button>
                </div>
              ) : (
                <button
                  onClick={() => (canPreview(a) ? setPreview(a) : download(a))}
                  data-testid={`attachment-download-${a.id}`}
                  className="truncate text-left text-sm font-medium text-sky-700 hover:underline"
                  title={a.original_filename}
                >
                  {simpleNaming ? (a.original_filename || a.name) : a.name}
                </button>
              )}
              <p className="truncate text-xs text-slate-400">
                {simpleNaming ? (a.size != null ? sizeLabel(a.size) : "") : `${a.original_filename}${a.size != null ? ` · ${sizeLabel(a.size)}` : ""}`}
              </p>
              <div className="mt-1 flex items-center gap-2">
                <Avatar name={a.uploaded_by_name} user className="h-5 w-5 text-[9px]" />
                <span className="text-xs text-slate-500">{a.uploaded_by_name || "Unknown"}</span>
                <span className="text-xs text-slate-400">{whenLabel(a.created_at)}</span>
              </div>
            </div>
            {editingId !== a.id && (
              <div className="flex items-start gap-0.5">
                {canPreview(a) && (
                  <button onClick={() => setPreview(a)} data-testid={`attachment-preview-${a.id}`} className="h-fit rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700" title="Preview">
                    <Eye className="h-3.5 w-3.5" />
                  </button>
                )}
                {!simpleNaming && (
                  <button onClick={() => startRename(a)} data-testid={`attachment-rename-${a.id}`} className="h-fit rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700" title="Rename">
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                )}
                <button onClick={() => download(a)} data-testid={`attachment-download-${a.id}`} className="h-fit rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700" title="Download">
                  <Download className="h-3.5 w-3.5" />
                </button>
                <DeleteButton
                  endpoint="/attachments"
                  id={a.id}
                  label="attachment"
                  onDeleted={() => setItems((prev) => prev.filter((x) => x.id !== a.id))}
                  trigger={
                    <button data-testid={`attachment-delete-${a.id}`} className="h-fit rounded-md p-1 text-rose-400 transition-colors hover:bg-red-50 hover:text-red-600" title="Delete attachment">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  }
                />
              </div>
            )}
          </div>
        ))}
      </div>

      <PreviewModal attachment={preview} onClose={() => setPreview(null)} />
    </div>
  );
}
